import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Button, Chip } from '@mui/material';
import {
  Bolt as FlashIcon,
  MusicNote as MusicIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import FlashQuizPracticeMain from './FlashQuizPracticeMain';
import AuthModal from '../../auth/AuthModal';
import InstaQuizShell from './InstaQuizShell';
import { useAuthContext } from '../../../../context/AuthContext';
import { instaQuizCardSx, instaQuizPrimaryButtonSx, INSTA_QUIZ } from './instaQuizTheme';

const FlashQuizPracticeWelcome = ({ onBack, onPlayLive }) => {
  const { isAuthenticated } = useAuthContext();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [view, setView] = useState('welcome'); // 'welcome' | 'playing'
  const [playSession, setPlaySession] = useState(0);
  const didAutoStartRef = useRef(false);

  useEffect(() => {
    if (isAuthenticated && view === 'welcome' && !didAutoStartRef.current) {
      didAutoStartRef.current = true;
      setPlaySession((n) => n + 1);
      setView('playing');
    }
  }, [isAuthenticated, view]);

  const handleAuthSuccess = useCallback(() => {
    setShowAuthModal(false);
    setPlaySession((n) => n + 1);
    setView('playing');
  }, []);

  const handleBackToWelcome = useCallback(() => {
    setView('welcome');
  }, []);

  const handleStartDemo = () => {
    if (isAuthenticated) {
      setPlaySession((n) => n + 1);
      setView('playing');
    } else {
      setShowAuthModal(true);
    }
  };

  if (view === 'welcome') {
    return (
      <>
        <InstaQuizShell onBack={onBack} title="Insta Quiz Demo">
          <Box sx={{ ...instaQuizCardSx, p: { xs: 3, sm: 5 }, textAlign: 'center', maxWidth: 520 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 88,
                height: 88,
                borderRadius: '50%',
                mb: 2.5,
                background:
                  'linear-gradient(135deg, rgba(156,39,176,0.3) 0%, rgba(126,87,194,0.2) 100%)',
                border: `2px solid ${INSTA_QUIZ.cardBorder}`,
              }}
            >
              <SchoolIcon sx={{ fontSize: 44, color: INSTA_QUIZ.purpleLight }} />
            </Box>

            <Chip
              label="DEMO MODE"
              size="small"
              sx={{
                mb: 2,
                bgcolor: 'rgba(156,39,176,0.15)',
                color: INSTA_QUIZ.purpleLight,
                border: `1px solid ${INSTA_QUIZ.cardBorder}`,
                fontWeight: 600,
              }}
            />

            <Typography
              variant="h4"
              sx={{ color: '#fff', fontWeight: 800, mb: 1.5, fontSize: { xs: '1.5rem', sm: '1.85rem' } }}
            >
              Warm up your music brain
            </Typography>

            <Typography
              variant="body1"
              sx={{ color: INSTA_QUIZ.textMuted, mb: 4, lineHeight: 1.75 }}
            >
              Try a sample quizlet with real gameplay — same buzz, same leaderboard energy,
              zero pressure. Perfect while you wait for the live show.
            </Typography>

            <Button
              variant="contained"
              size="large"
              onClick={handleStartDemo}
              startIcon={<FlashIcon />}
              sx={{ ...instaQuizPrimaryButtonSx, mb: 2 }}
            >
              {isAuthenticated ? 'Start Demo' : 'Sign In & Try Demo'}
            </Button>

            {onPlayLive && (
              <Button
                variant="text"
                onClick={onPlayLive}
                startIcon={<MusicIcon />}
                sx={{
                  color: INSTA_QUIZ.purpleLight,
                  textTransform: 'none',
                  fontWeight: 600,
                  display: 'block',
                  mx: 'auto',
                  '&:hover': { bgcolor: 'rgba(156,39,176,0.1)' },
                }}
              >
                Jump to live Insta Quiz instead
              </Button>
            )}
          </Box>
        </InstaQuizShell>
        <AuthModal
          open={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onSuccess={handleAuthSuccess}
        />
      </>
    );
  }

  return (
    <FlashQuizPracticeMain
      key={playSession}
      onBack={onBack}
      onBackToWelcome={handleBackToWelcome}
      onPlayLive={onPlayLive}
    />
  );
};

export default FlashQuizPracticeWelcome;
